/**
 * KempenRekodView — REKOD KEMPEN PROMOSI (tab admin "Kempen")
 *
 * ⛔ INI BUKU REKOD, BUKAN SUIS KAWALAN.
 *    Apa yang pelanggan BENAR-BENAR dapat masa bayar ditentukan oleh server
 *    (pemalar PREMIUM_MODULES / BILLPLZ_COMBO_MODULES dalam functions/index.js)
 *    + flag `isPremiumAddon` pada setiap module. Menyimpan kempen di sini TIDAK
 *    mengubah apa-apa untuk pelanggan. Tujuannya: supaya sesiapa yang menyemak
 *    akaun pelanggan kemudian hari tahu kempen mana yang berjalan pada tarikh itu,
 *    tanpa perlu mengorek balik order, commit dan nota.
 *
 * Sebab ia wujud: audit 2026-09-08 mendapati tiada satu pun rekod bertarikh tentang
 * kempen Triple Combo (Julai 2026). Akibatnya kiraan "siapa berhak" tersilap DUA KALI
 * sebelum tarikh sebenar dijumpai daripada suis jualan.
 *
 * ── SIMPANAN ──
 * SATU DOC SETIAP KEMPEN: `config/campaign_records/items/{id}`.
 *   ⚠️ Sengaja BUKAN satu array dalam satu doc. Senarai kempen membesar tanpa had
 *      sepanjang hayat produk, dan prinsip projek melarang senarai yang boleh
 *      membesar duduk dalam satu doc (had keras Firestore 1 MB).
 *   ⚠️ Subkoleksi TIDAK mewarisi rule `match /config/{docId}` — ia ada rule sendiri
 *      dalam firestore.rules. Kalau tab ini memberi "permission denied", rules belum
 *      dideploy; skrin akan menyatakannya terus, bukan gagal senyap.
 *   ⚠️ JANGAN keliru dengan collection `campaigns` sedia ada — itu preset yang
 *      dipakai borang "Aktifkan Bayaran" (adminApprovePayment). Yang ini berasingan
 *      dan sengaja tidak menyentuhnya (arahan user 2026-09-08: "asingkan dulu").
 *
 * ── KIRAAN IMPAK ──
 * "Pelanggan baharu dalam tempoh" = bilangan doc `licenses` yang `createdAt`-nya
 * jatuh dalam julat kempen. Guna getCountFromServer supaya tidak membaca ratusan doc.
 *   ⚠️⚠️ KEMBAR: definisi yang SAMA dipakai oleh skrip pembetulan akses
 *      `betulkan_akses_module.cjs` (root projek) untuk menentukan siapa layak.
 *      Ubah satu, ubah dua-dua — kalau tidak tab ini dan skrip itu akan memberi
 *      nombor berbeza untuk kempen yang sama, dan tiada siapa akan perasan.
 *   ⚠️ Disahkan 2026-09-09: 466/466 lesen ADA `createdAt` (0 tercicir daripada julat),
 *      dan 413/413 order berbayar ADA `paidAt` (0 tercicir, 0 salah kira).
 *   ⛔ JANGAN guna tarikh order sebagai ganti — 33 order berbayar ialah Flow Ultra
 *      (RM100 akaun Google) yang LANGSUNG tidak mencipta lesen SESAAT.
 *   ⛔ JANGAN guna `payment_submissions.submittedAt` — itu tarikh pelanggan HANTAR
 *      BUKTI, bukan tarikh dia jadi pelanggan. Beza itu menyebabkan 4 lesen
 *      tersalah kelas semasa audit pertama.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import {
    doc, setDoc, deleteDoc, collection, getDocs, query, where, orderBy,
    getCountFromServer, serverTimestamp, limit as firestoreLimit
} from 'firebase/firestore';
import {
    Plus, Pencil, Trash2, RefreshCw, Calendar, AlertTriangle, X,
    ChevronDown, ChevronRight, Users, Info, Save
} from 'lucide-react';

// Satu doc setiap kempen.
const koleksiKempen = () => collection(db, 'config', 'campaign_records', 'items');
const docKempen = (id) => doc(db, 'config', 'campaign_records', 'items', id);

// Pakej yang boleh dikaitkan dengan satu kempen. Satu kempen boleh membawa
// BEBERAPA pakej dengan harga berbeza — cth Triple Combo: Premium baharu RM199,
// bundle sambung RM168, add-on berasingan RM69.
// ⚠️ KEMBAR: harga yang BENAR-BENAR dicaj hidup di dua tempat lain —
//    functions/index.js (BILLPLZ_PLANS) dan preset borang "Aktifkan Bayaran"
//    dalam AdminView.jsx. Senarai di sini untuk REKOD sahaja; ia tidak
//    menetapkan harga sesiapa. Kalau pakej baharu dijual, tambah di sini juga.
const PAKEJ = [
    { id: 'premium_baru', label: 'Premium — Baharu' },
    { id: 'premium_renew', label: 'Premium — Sambung' },
    // Bundle sambung + add-on dalam SATU bil (RM99 + RM69 = RM168). Ia pilihan
    // berasingan, bukan "Premium — Sambung" berharga lain: 13 pelanggan membeli
    // cara ini (6–31 Jul 2026) dan module yang mereka terima berbeza daripada
    // sambung biasa. Dua baris berlainan = rekod yang jujur.
    { id: 'bundle_renew_addon', label: 'Bundle: Sambung + Add-on' },
    { id: 'fighter', label: 'Fighter — Tahunan' },
    { id: 'smart', label: 'Pakej SMART' },
    { id: 'addon_flownity', label: 'Add-on FLOWNITY' },
    { id: 'flowultra', label: 'Flow Ultra (Sharing)' },
    // Untuk pelepasan percuma yang bukan pembelian — cth GOYANG dibuka kepada
    // semua pelanggan berbayar sedia ada. Harga dibiar kosong.
    { id: 'sedia_ada', label: 'Semua pelanggan sedia ada' },
    { id: 'lain', label: 'Lain-lain' },
];
const namaPakej = (id) => PAKEJ.find(p => p.id === id)?.label || id;

// ⭐ Module dicatat PER PAKEJ, bukan satu senarai untuk seluruh kempen.
//    Sebabnya nyata pada kempen Triple Combo: Premium RM199 memberi enam module,
//    tetapi Add-on RM69 hanya memberi tiga. Satu senarai sekempen akan menipu.
//    `k.modules` aras-kempen KEKAL ditulis sebagai GABUNGAN semua baris — ia
//    ringkasan untuk paparan, bukan medan yang disunting. Jangan sunting ia terus.
//
// Rekod lama menyimpan satu harga + satu senarai module untuk seluruh kempen.
// Fungsi ini menukarnya ke bentuk baris supaya tiada data hilang bila dibuka.
const baikiPakej = (k) => {
    const modKempen = Array.isArray(k.modules) ? k.modules : [];
    if (Array.isArray(k.pakej) && k.pakej.length) {
        return k.pakej.map(p => ({
            id: p.id,
            harga: p.harga || '',
            // Baris lama (sebelum module per pakej) mewarisi senarai kempen —
            // lebih baik daripada kosong; user tinggal buang yang tak berkenaan.
            modules: Array.isArray(p.modules) ? p.modules : modKempen,
        }));
    }
    if (k.hargaRM) return [{ id: 'lain', harga: String(k.hargaRM), modules: modKempen }];
    if (modKempen.length) return [{ id: 'sedia_ada', harga: '', modules: modKempen }];
    return [];
};

// Gabungan module semua baris — ringkasan aras-kempen.
const gabungModule = (rows) => [...new Set((rows || []).flatMap(p => p.modules || []))];

// Module yang TIADA doc dalam collection `modules` (didaftar dalam kod sahaja).
const MODULE_TAMBAHAN = [
    { id: 'smartcustom', name: 'SMART' },
    { id: 'fbreels', name: 'FB Reels' },
];

// ─── Tarikh: semua input jenis 'YYYY-MM-DD', ditafsir waktu Malaysia ───
const mulaHari = (s) => new Date(`${s}T00:00:00+08:00`);
const hujungHari = (s) => new Date(`${s}T23:59:59+08:00`);
const hariIni = () => new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 10);
const paparTarikh = (s) => {
    if (!s) return '—';
    const [y, m, d] = s.split('-');
    const bulan = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogos', 'Sep', 'Okt', 'Nov', 'Dis'];
    return `${d} ${bulan[Number(m) - 1] || m} ${y}`;
};

const kempenKosong = () => ({
    id: `k_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    nama: '',
    mula: hariIni(),
    tamat: '',
    modules: [],        // GABUNGAN semua baris — dikira semasa simpan, bukan disunting
    pakej: [],          // [{ id, harga, modules[] }] — harga RM; kosong = percuma
    nota: '',
});

// Dua kempen bertindih kalau tempohnya bersilang. Tamat kosong = masih berjalan.
const bertindih = (a, b) => {
    const a1 = mulaHari(a.mula).getTime();
    const a2 = a.tamat ? hujungHari(a.tamat).getTime() : Infinity;
    const b1 = mulaHari(b.mula).getTime();
    const b2 = b.tamat ? hujungHari(b.tamat).getTime() : Infinity;
    return a1 <= b2 && b1 <= a2;
};

// Kegagalan mesti MENYATAKAN puncanya, bukan sekadar "gagal".
const bacaRalat = (e, kerja) => {
    if (e?.code === 'permission-denied') {
        return 'Firestore menolak capaian. Rules untuk rekod kempen belum dideploy — '
            + 'jalankan: firebase deploy --only firestore:rules';
    }
    if (e?.code === 'failed-precondition') {
        return 'Firestore minta index untuk query ini. Buka Console (F12) — pautan cipta index ada dalam mesej ralat.';
    }
    return `${kerja} gagal: ${e?.message || 'ralat tidak diketahui'}`;
};

export default function KempenRekodView() {
    const [senarai, setSenarai] = useState([]);
    const [modules, setModules] = useState([]);
    const [memuat, setMemuat] = useState(true);
    const [menyimpan, setMenyimpan] = useState(false);
    const [ralat, setRalat] = useState('');
    const [edit, setEdit] = useState(null);          // objek kempen sedang disunting
    const [impak, setImpak] = useState({});          // id -> {pelanggan, order} | 'memuat' | 'ralat'
    const [buka, setBuka] = useState({});            // id -> true (senarai pelanggan dibuka)
    const [senaraiUser, setSenaraiUser] = useState({}); // id -> array | 'memuat' | 'ralat'

    // ─── Muat rekod + senarai module ───
    const muat = useCallback(async () => {
        setMemuat(true); setRalat('');
        try {
            const [snapKempen, snapModul] = await Promise.all([
                getDocs(koleksiKempen()),
                getDocs(collection(db, 'modules')),
            ]);
            setSenarai(snapKempen.docs.map(d => ({ id: d.id, ...d.data() })));

            const mods = snapModul.docs.map(d => ({ id: d.id, name: d.data().name || d.id }));
            for (const m of MODULE_TAMBAHAN) {
                if (!mods.some(x => x.id === m.id)) mods.push(m);
            }
            mods.sort((a, b) => a.name.localeCompare(b.name));
            setModules(mods);
        } catch (e) {
            console.warn('[Kempen] muat gagal', e);
            setRalat(bacaRalat(e, 'Memuat rekod kempen'));
        } finally {
            setMemuat(false);
        }
    }, []);

    useEffect(() => { muat(); }, [muat]);

    // ─── Simpan SATU kempen (satu doc) ───
    const simpanKempen = async () => {
        if (!edit) return;
        const nama = (edit.nama || '').trim();
        if (!nama) { setRalat('Nama kempen wajib diisi.'); return; }
        if (!edit.mula) { setRalat('Tarikh mula wajib dipilih.'); return; }
        if (edit.tamat && edit.tamat < edit.mula) { setRalat('Tarikh tamat tidak boleh sebelum tarikh mula.'); return; }
        // Harga mesti nombor. Kosong dibenarkan (kempen percuma), huruf tidak —
        // kalau tidak rekod harga jadi teks yang tiada siapa boleh jumlahkan kelak.
        const hargaRosak = (edit.pakej || []).find(p => p.harga && !/^\d+(\.\d{1,2})?$/.test(String(p.harga).trim()));
        if (hargaRosak) { setRalat(`Harga "${hargaRosak.harga}" bukan nombor. Isi angka sahaja (cth 199), atau kosongkan kalau percuma.`); return; }

        setMenyimpan(true); setRalat('');
        const { id, ...isi } = edit;
        const baris = (isi.pakej || [])
            .filter(p => p && p.id)
            .map(p => ({
                id: p.id,
                harga: String(p.harga || '').trim(),
                modules: Array.isArray(p.modules) ? p.modules : [],
            }));
        const rekod = {
            nama,
            mula: edit.mula,
            tamat: edit.tamat || '',
            pakej: baris,
            // Ringkasan sahaja — DIKIRA daripada baris, tidak pernah disunting terus.
            // Kalau ia disunting berasingan, dua sumber kebenaran akan terpesong.
            modules: gabungModule(baris),
            hargaRM: '',   // medan lama — dikosongkan supaya tiada dua sumber harga
            nota: isi.nota || '',
            updatedAt: serverTimestamp(),
        };
        try {
            await setDoc(docKempen(id), rekod, { merge: true });
            const ada = senarai.some(k => k.id === id);
            setSenarai(ada
                ? senarai.map(k => (k.id === id ? { id, ...rekod } : k))
                : [...senarai, { id, ...rekod }]);
            setEdit(null);
        } catch (e) {
            console.warn('[Kempen] simpan gagal', e);
            setRalat(bacaRalat(e, 'Menyimpan kempen'));
        } finally {
            setMenyimpan(false);
        }
    };

    const padamKempen = async (k) => {
        if (!window.confirm(`Padam kempen "${k.nama}"?\n\nRekod ini rujukan sahaja — memadamnya tidak menyentuh akses mana-mana pelanggan.`)) return;
        setRalat('');
        try {
            await deleteDoc(docKempen(k.id));
            setSenarai(senarai.filter(x => x.id !== k.id));
        } catch (e) {
            console.warn('[Kempen] padam gagal', e);
            setRalat(bacaRalat(e, 'Memadam kempen'));
        }
    };

    // ─── Kira berapa pelanggan masuk dalam tempoh kempen ───
    // ⚠️ KEMBAR dengan betulkan_akses_module.cjs — lihat nota kepala fail.
    // Julat pada SATU medan sahaja (createdAt / paidAt) → tiada composite index diperlukan.
    const kiraImpak = async (k) => {
        setImpak(p => ({ ...p, [k.id]: 'memuat' }));
        try {
            const a = mulaHari(k.mula);
            const b = k.tamat ? hujungHari(k.tamat) : new Date();
            const [cLesen, cOrder] = await Promise.all([
                getCountFromServer(query(collection(db, 'licenses'),
                    where('createdAt', '>=', a), where('createdAt', '<=', b))),
                getCountFromServer(query(collection(db, 'orders'),
                    where('paidAt', '>=', a), where('paidAt', '<=', b))),
            ]);
            setImpak(p => ({ ...p, [k.id]: { pelanggan: cLesen.data().count, order: cOrder.data().count } }));
        } catch (e) {
            console.warn('[Kempen] kira impak gagal', e);
            setImpak(p => ({ ...p, [k.id]: 'ralat' }));
            setRalat(bacaRalat(e, 'Mengira impak'));
        }
    };

    const kiraSemua = async () => { for (const k of senarai) await kiraImpak(k); };

    // ─── Senarai pelanggan yang masuk dalam tempoh ───
    const muatSenaraiUser = async (k) => {
        setSenaraiUser(p => ({ ...p, [k.id]: 'memuat' }));
        try {
            const a = mulaHari(k.mula);
            const b = k.tamat ? hujungHari(k.tamat) : new Date();
            const snap = await getDocs(query(collection(db, 'licenses'),
                where('createdAt', '>=', a), where('createdAt', '<=', b),
                orderBy('createdAt', 'asc'), firestoreLimit(500)));
            const rows = snap.docs.map(d => {
                const L = d.data();
                return {
                    uid: d.id,
                    email: L.email || '(tiada emel)',
                    pada: L.createdAt?.toDate ? L.createdAt.toDate().toISOString().slice(0, 10) : '—',
                    pakej: L.type || '—',
                    modules: Array.isArray(L.allowedModules) ? L.allowedModules : [],
                };
            });
            setSenaraiUser(p => ({ ...p, [k.id]: rows }));
        } catch (e) {
            console.warn('[Kempen] senarai pelanggan gagal', e);
            setSenaraiUser(p => ({ ...p, [k.id]: 'ralat' }));
            setRalat(bacaRalat(e, 'Memuat senarai pelanggan'));
        }
    };

    const togglBuka = (k) => {
        const nak = !buka[k.id];
        setBuka(p => ({ ...p, [k.id]: nak }));
        if (nak && !senaraiUser[k.id]) muatSenaraiUser(k);
    };

    // Susun terbaru dahulu
    const tersusun = useMemo(
        () => [...senarai].sort((a, b) => String(b.mula || '').localeCompare(String(a.mula || ''))),
        [senarai]
    );

    // Cari kempen yang tempohnya bertindih (bukan ralat — cuma perlu disedari)
    const idBertindih = useMemo(() => {
        const set = new Set();
        for (let i = 0; i < senarai.length; i++) {
            for (let j = i + 1; j < senarai.length; j++) {
                if (bertindih(senarai[i], senarai[j])) { set.add(senarai[i].id); set.add(senarai[j].id); }
            }
        }
        return set;
    }, [senarai]);

    const namaModule = (id) => modules.find(m => m.id === id)?.name || id;

    // ─── Rupa ───
    const kadKelas = 'bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800';
    const inputKelas = 'w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100';

    return (
        <div className="space-y-4">
            {/* Kepala */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <Calendar size={18} className="text-indigo-500" /> Rekod Kempen Promosi
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Catat setiap kempen: bila ia mula, bila tamat, dan module apa yang ia beri.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={muat} disabled={memuat}
                        className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200">
                        <RefreshCw size={14} className={memuat ? 'animate-spin' : ''} /> Muat semula
                    </button>
                    {senarai.length > 0 && (
                        <button onClick={kiraSemua}
                            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200">
                            <Users size={14} /> Kira semua impak
                        </button>
                    )}
                    <button onClick={() => { setRalat(''); setEdit(kempenKosong()); }}
                        className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold flex items-center gap-2">
                        <Plus size={14} /> Kempen Baharu
                    </button>
                </div>
            </div>

            {/* Amaran: rekod sahaja */}
            <div className="rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 flex gap-2">
                <Info size={16} className="text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                    <strong>Ini buku rekod, bukan suis kawalan.</strong> Menyimpan kempen di sini
                    <strong> tidak mengubah</strong> apa yang pelanggan dapat masa bayar — itu ditentukan
                    oleh server. Tujuannya supaya sejarah kempen ada tarikhnya, dan tiada siapa perlu
                    meneka semula kemudian hari.
                </div>
            </div>

            {ralat && (
                <div className="rounded-xl border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 p-3 text-xs text-rose-800 dark:text-rose-200 flex items-start gap-2">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span className="whitespace-pre-wrap break-words">{ralat}</span>
                </div>
            )}

            {/* Borang tambah/sunting */}
            {edit && (
                <div className={`${kadKelas} p-4 space-y-3`}>
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                            {senarai.some(k => k.id === edit.id) ? 'Sunting kempen' : 'Kempen baharu'}
                        </h3>
                        <button onClick={() => { setEdit(null); setRalat(''); }} className="text-slate-400 hover:text-slate-600">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Nama kempen</label>
                            <input className={inputKelas} value={edit.nama} placeholder="cth: TRIPLE COMBO RM199 (Julai)"
                                onChange={e => setEdit({ ...edit, nama: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">Tarikh mula</label>
                            <input type="date" className={inputKelas} value={edit.mula}
                                onChange={e => setEdit({ ...edit, mula: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                                Tarikh tamat <span className="font-normal text-slate-400">(kosongkan = masih berjalan)</span>
                            </label>
                            <input type="date" className={inputKelas} value={edit.tamat || ''} min={edit.mula}
                                onChange={e => setEdit({ ...edit, tamat: e.target.value })} />
                        </div>
                        {/* Pakej + harga + module — SATU blok. Module dicatat per pakej sebab
                            satu kempen boleh menjual beberapa pakej yang memberi module berbeza
                            (Premium RM199 = 6 module · Add-on RM69 = 3 module sahaja). */}
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                                Pakej, harga &amp; module <span className="font-normal text-slate-400">(setiap pakej ada senarai module sendiri)</span>
                            </label>
                            <div className="space-y-2">
                                {(edit.pakej || []).map((baris, i) => (
                                    <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 space-y-2 bg-slate-50/60 dark:bg-slate-950/40">
                                        <div className="flex items-center gap-2">
                                            <select className={`${inputKelas} flex-1`} value={baris.id}
                                                onChange={e => {
                                                    const p = [...edit.pakej];
                                                    p[i] = { ...p[i], id: e.target.value };
                                                    setEdit({ ...edit, pakej: p });
                                                }}>
                                                {PAKEJ.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                                            </select>
                                            <div className="relative w-32 shrink-0">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">RM</span>
                                                <input className={`${inputKelas} pl-9`} value={baris.harga} placeholder="199" inputMode="decimal"
                                                    onChange={e => {
                                                        const p = [...edit.pakej];
                                                        p[i] = { ...p[i], harga: e.target.value };
                                                        setEdit({ ...edit, pakej: p });
                                                    }} />
                                            </div>
                                            <button type="button" title="Buang pakej ini"
                                                onClick={() => setEdit({ ...edit, pakej: edit.pakej.filter((_, j) => j !== i) })}
                                                className="p-2 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-400 hover:text-rose-600 shrink-0">
                                                <X size={14} />
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 items-center">
                                            <span className="text-[11px] text-slate-400 mr-1">Module:</span>
                                            {modules.map(m => {
                                                const pilih = (baris.modules || []).includes(m.id);
                                                return (
                                                    <button key={m.id} type="button"
                                                        onClick={() => {
                                                            const p = [...edit.pakej];
                                                            const sedia = p[i].modules || [];
                                                            p[i] = {
                                                                ...p[i],
                                                                modules: pilih ? sedia.filter(x => x !== m.id) : [...sedia, m.id],
                                                            };
                                                            setEdit({ ...edit, pakej: p });
                                                        }}
                                                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${pilih
                                                            ? 'bg-indigo-600 border-indigo-600 text-white'
                                                            : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}>
                                                        {m.name}
                                                    </button>
                                                );
                                            })}
                                            {(baris.modules || []).length === 0 && (
                                                <span className="text-[11px] text-amber-600 dark:text-amber-500 ml-1">belum pilih</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <button type="button"
                                    onClick={() => setEdit({ ...edit, pakej: [...(edit.pakej || []), { id: PAKEJ[0].id, harga: '', modules: [] }] })}
                                    className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                                    <Plus size={12} /> Tambah pakej
                                </button>
                                {(!edit.pakej || edit.pakej.length === 0) && (
                                    <p className="text-[11px] text-slate-400">
                                        Belum ada pakej. Untuk module yang dibuka percuma kepada pelanggan
                                        sedia ada, tambah satu pakej dan pilih “Semua pelanggan sedia ada”,
                                        biarkan harga kosong.
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                                Nota <span className="font-normal text-slate-400">(syarat, siapa layak, kenapa ditutup)</span>
                            </label>
                            <textarea className={`${inputKelas} min-h-[70px]`} value={edit.nota}
                                onChange={e => setEdit({ ...edit, nota: e.target.value })} />
                        </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                        <button onClick={simpanKempen} disabled={menyimpan}
                            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                            <Save size={14} /> {menyimpan ? 'Menyimpan…' : 'Simpan'}
                        </button>
                        <button onClick={() => { setEdit(null); setRalat(''); }}
                            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200">
                            Batal
                        </button>
                    </div>
                </div>
            )}

            {/* Senarai kempen */}
            {memuat ? (
                <div className={`${kadKelas} p-8 text-center text-sm text-slate-500`}>Memuat…</div>
            ) : tersusun.length === 0 ? (
                <div className={`${kadKelas} p-8 text-center`}>
                    <p className="text-sm text-slate-500">Belum ada kempen direkodkan.</p>
                    <p className="text-xs text-slate-400 mt-1">Tekan “Kempen Baharu” dan mula dari kempen 1 Julai 2026.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {tersusun.map(k => {
                        const im = impak[k.id];
                        const rows = senaraiUser[k.id];
                        return (
                            <div key={k.id} className={kadKelas}>
                                <div className="p-4">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-bold text-slate-900 dark:text-slate-100 text-sm">{k.nama}</h3>
                                                {baikiPakej(k).length > 0 && (
                                                    <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                                                        {baikiPakej(k).length} pakej
                                                    </span>
                                                )}
                                                {!k.tamat && (
                                                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                                                        Masih berjalan
                                                    </span>
                                                )}
                                                {idBertindih.has(k.id) && (
                                                    <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950 text-[11px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                                                        <AlertTriangle size={10} /> Bertindih
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                                                <Calendar size={12} />
                                                {paparTarikh(k.mula)} → {k.tamat ? paparTarikh(k.tamat) : 'sekarang'}
                                            </p>
                                            {/* Satu baris setiap pakej — pakej · harga → module yang ia beri */}
                                            <div className="mt-2 space-y-1.5">
                                                {baikiPakej(k).length === 0 ? (
                                                    <span className="text-[11px] text-slate-400">Tiada pakej dicatat</span>
                                                ) : baikiPakej(k).map((p, i) => (
                                                    <div key={i} className="flex items-start gap-2 flex-wrap">
                                                        <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[11px] font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                                                            {namaPakej(p.id)}{p.harga ? ` · RM${p.harga}` : ' · percuma'}
                                                        </span>
                                                        <span className="text-[11px] text-slate-400 mt-0.5">→</span>
                                                        <span className="flex flex-wrap gap-1">
                                                            {(p.modules || []).length === 0
                                                                ? <span className="text-[11px] text-amber-600 dark:text-amber-500 mt-0.5">module belum dicatat</span>
                                                                : p.modules.map(id => (
                                                                    <span key={id} className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/50 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">
                                                                        {namaModule(id)}
                                                                    </span>
                                                                ))}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            {k.nota && <p className="text-[11px] text-slate-500 mt-2 whitespace-pre-wrap">{k.nota}</p>}
                                        </div>

                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <button onClick={() => { setRalat(''); setEdit({ ...k, modules: k.modules || [], pakej: baikiPakej(k) }); }} title="Sunting"
                                                className="p-2 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-500 hover:text-indigo-600">
                                                <Pencil size={14} />
                                            </button>
                                            <button onClick={() => padamKempen(k)} title="Padam"
                                                className="p-2 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-500 hover:text-rose-600">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Impak */}
                                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-3 flex-wrap">
                                        {im === 'memuat' ? (
                                            <span className="text-xs text-slate-500 flex items-center gap-1.5">
                                                <RefreshCw size={12} className="animate-spin" /> Mengira…
                                            </span>
                                        ) : im === 'ralat' ? (
                                            <span className="text-xs text-rose-600">Gagal mengira impak — lihat mesej di atas</span>
                                        ) : im ? (
                                            <>
                                                <span className="text-xs text-slate-700 dark:text-slate-200">
                                                    <strong className="text-base font-bold text-indigo-600 dark:text-indigo-400">{im.pelanggan}</strong>
                                                    <span className="ml-1.5">pelanggan baharu dalam tempoh ini</span>
                                                </span>
                                                <span className="text-xs text-slate-500">·</span>
                                                <span className="text-xs text-slate-500">{im.order} bayaran diterima</span>
                                                <button onClick={() => togglBuka(k)}
                                                    className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-1 ml-auto">
                                                    {buka[k.id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                    {buka[k.id] ? 'Sorok senarai' : 'Lihat senarai'}
                                                </button>
                                            </>
                                        ) : (
                                            <button onClick={() => kiraImpak(k)}
                                                className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                                                <Users size={12} /> Kira berapa pelanggan terkesan
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Senarai pelanggan */}
                                {buka[k.id] && (
                                    <div className="border-t border-slate-100 dark:border-slate-800 max-h-80 overflow-auto">
                                        {rows === 'memuat' ? (
                                            <p className="p-4 text-xs text-slate-500">Memuat senarai…</p>
                                        ) : rows === 'ralat' ? (
                                            <p className="p-4 text-xs text-rose-600">Gagal memuat senarai — lihat mesej di atas.</p>
                                        ) : !rows || rows.length === 0 ? (
                                            <p className="p-4 text-xs text-slate-500">Tiada pelanggan dalam tempoh ini.</p>
                                        ) : (
                                            <table className="w-full text-xs">
                                                <thead className="bg-slate-50 dark:bg-slate-950 sticky top-0">
                                                    <tr className="text-slate-500">
                                                        <th className="px-3 py-2 text-left font-semibold">Emel</th>
                                                        <th className="px-3 py-2 text-left font-semibold">Masuk</th>
                                                        <th className="px-3 py-2 text-left font-semibold">Pakej</th>
                                                        <th className="px-3 py-2 text-left font-semibold">Module sekarang</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map(r => (
                                                        <tr key={r.uid} className="border-t border-slate-100 dark:border-slate-800">
                                                            <td className="px-3 py-2 text-slate-700 dark:text-slate-200 whitespace-normal break-all">{r.email}</td>
                                                            <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{r.pada}</td>
                                                            <td className="px-3 py-2 text-slate-500">{r.pakej}</td>
                                                            <td className="px-3 py-2 text-slate-500 whitespace-normal">
                                                                {r.modules.length ? r.modules.map(namaModule).join(', ') : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                        {Array.isArray(rows) && rows.length >= 500 && (
                                            <p className="px-3 py-2 text-[11px] text-amber-600 border-t border-slate-100 dark:border-slate-800">
                                                Dipaparkan 500 pertama sahaja.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <p className="text-[11px] text-slate-400 pt-1">
                “Pelanggan baharu” = lesen yang <strong>dicipta</strong> dalam tempoh kempen. Definisi
                yang sama dipakai oleh skrip pembetulan akses <code>betulkan_akses_module.cjs</code> —
                kalau salah satu diubah, ubah dua-dua.
            </p>
        </div>
    );
}
